import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { Button } from 'antd';

import { desktop, tablet } from 'services/ui';
import { IResumeFields, Languages } from 'context';
import { EducationBlock } from 'organisms/EducationBlock';
import { ExperienceBlock } from 'organisms/ExperienceBlock';
import { LanguageBlock } from 'organisms/LanguageBlock';
import { ProfileBasicsBlock } from 'organisms/ProfileBasicsBlock';
import { ProfileCertificatesBlock } from 'organisms/ProfileCertificatesBlock';
import { ProfileProjectsBlock } from 'organisms/ProfileProjectsBlock';
import { ProfileSkillsBlock } from 'organisms/ProfileSkillsBlock';
import type { ICareerProfileFormValues } from './types';

export interface ICareerProfileForm {
  values: ICareerProfileFormValues;
  fields: IResumeFields;
  /** Language for the reused date pickers — English keeps months deterministic. */
  dateLanguage: Languages;
  onChange: (name: string, value: unknown) => void;
  onSave: () => void;
  saving: boolean;
}

/**
 * The language-neutral profile editor (spec 0018, increment B). Reuses the
 * Experience / Education / Language blocks from the old resume, repointed at the
 * new profile model; skills, projects and certificates are new.
 */
export const CareerProfileForm = memo(
  ({ values, fields, dateLanguage, onChange, onSave, saving }: ICareerProfileForm) => {
    const { t } = useTranslation();

    return (
      <Wrapper>
        <ProfileBasicsBlock values={values} onChange={onChange} />

        <ProfileSkillsBlock
          skills={values.skills}
          customSkills={values.customSkills}
          onChangeSkills={(skills) => onChange('skills', skills)}
          onChangeCustomSkills={(customSkills) => onChange('customSkills', customSkills)}
        />

        <ProfileProjectsBlock
          projects={values.projects}
          onChange={(projects) => onChange('projects', projects)}
        />

        <ExperienceBlock
          language={dateLanguage}
          fields={fields}
          values={values.experience}
          onExperienceChange={onChange}
          onInputChange={onChange}
        />

        <EducationBlock
          language={dateLanguage}
          fields={fields}
          values={values.education}
          onChangeEducation={onChange}
          onInputChange={onChange}
        />

        <LanguageBlock
          fields={fields}
          languagesList={values.languages}
          onChangeLanguages={onChange}
          onInputChange={onChange}
        />

        <ProfileCertificatesBlock
          certificates={values.certificates}
          onChange={(certificates) => onChange('certificates', certificates)}
        />

        <SaveRow>
          <Button type="primary" size="large" loading={saving} onClick={onSave}>
            {t('careerProfile.save')}
          </Button>
        </SaveRow>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SaveRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 24px 0;

  ${tablet} {
    padding: 12px 0;
  }

  ${desktop} {
    padding: 16px 0;
  }
`;
